import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { NavLink, Link } from 'react-router-dom';
import { Menu, X, Linkedin, ChevronDown, Brain, Database, CloudLightning, Network, Lock, Bot, Key, BarChart3, GitHub, Mail, Globe, Heart } from 'lucide-react';
import './MainLayout.css';
import CookieConsent from '../components/CookieConsent';

const featuresDropdownData = [
    {
        icon: Brain,
        title: "AI-Powered Search & Chat",
        description: "Advanced AI that understands context and intent",
        path: "/features/ai-search-chat"
    },
    {
        icon: Database,
        title: "Document Management",
        description: "Comprehensive document organization system",
        path: "/features/document-management"
    },
    {
        icon: CloudLightning,
        title: "BidAI & Contracts",
        description: "Automated RFP and contract analysis",
        path: "/features/bidai-contract-analysis"
    },
    {
        icon: Network,
        title: "RecruitAI",
        description: "Intelligent talent matching and assessment",
        path: "/features/recruitai-talent"
    },
    {
        icon: Lock,
        title: "Enterprise Security",
        description: "Military-grade security measures",
        path: "/features/enterprise-security"
    },
    {
        icon: Bot,
        title: "AI Assistant",
        description: "Context-aware AI companion",
        path: "/features/ai-assistant"
    },
    {
        icon: Key,
        title: "Integrations",
        description: "Connect with your existing tools",
        path: "/features/integrations"
    },
    {
        icon: BarChart3,
        title: "Analytics",
        description: "Insights into your knowledge base",
        path: "/features/analytics-insights"
    }
];

const FeaturesDropdown = ({ isOpen, onClose }) => (
    <AnimatePresence>
        {isOpen && (
            <motion.div
                className="absolute z-10 mt-2 w-screen max-w-md transform px-4 sm:px-0 lg:max-w-2xl bg-white rounded-lg shadow-lg ring-1 ring-black ring-opacity-5 overflow-hidden"
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                transition={{ duration: 0.2 }}
            >
                <div className="p-6 grid gap-6 sm:grid-cols-2 sm:gap-8 lg:grid-cols-2">
                    {featuresDropdownData.map((feature, index) => {
                        const Icon = feature.icon;
                        return (
                            <Link
                                key={index}
                                to={feature.path}
                                className="-m-3 p-3 flex items-start rounded-lg hover:bg-gray-50 transition ease-in-out duration-150"
                                onClick={onClose}
                            >
                                <div className="flex-shrink-0 flex items-center justify-center h-10 w-10 rounded-md bg-blue-100 text-blue-600 sm:h-12 sm:w-12">
                                    <Icon size={24} aria-hidden="true" />
                                </div>
                                <div className="ml-4">
                                    <p className="text-base font-medium text-gray-900">{feature.title}</p>
                                    <p className="mt-1 text-sm text-gray-500">{feature.description}</p>
                                </div>
                            </Link>
                        );
                    })}
                </div>
                <div className="px-5 py-5 bg-gray-50 space-y-6 sm:flex sm:space-y-0 sm:space-x-10 sm:px-8">
                    <div className="flow-root">
                        <Link
                            to="/features"
                            className="-m-3 p-3 flex items-center rounded-md text-base font-medium text-gray-900 hover:bg-gray-100 transition ease-in-out duration-150"
                            onClick={onClose}
                        >
                            <span className="ml-3">View All Features</span>
                            <ChevronDown className="ml-2 h-5 w-5 text-gray-400" aria-hidden="true"/>
                        </Link>
                    </div>
                </div>
            </motion.div>
        )}
    </AnimatePresence>
);

const MainLayout = ({ children }) => {
    const [isOpen, setIsOpen] = useState(false);
    const [isDropdownOpen, setIsDropdownOpen] = useState(false);
    const dropdownRef = useRef(null);
    const [email, setEmail] = useState('');
    const [subscribeStatus, setSubscribeStatus] = useState('idle'); // 'idle' | 'loading' | 'success' | 'error'
    const [subscribeMessage, setSubscribeMessage] = useState('');

    const menuItems = [
        { path: '/solutions', label: 'Solutions' },
        { path: '/why-ufficient', label: 'Why Ufficient' },
        { path: '/trust', label: 'Trust' },
        { path: '/about', label: 'About' },
        // { path: '/resources', label: 'Resources' },
    ];

    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setIsDropdownOpen(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const handleSubscribe = async () => {
        if (!email || !/\S+@\S+\.\S+/.test(email)) {
            setSubscribeStatus('error');
            setSubscribeMessage('Please enter a valid email address.');
            return;
        }

        setSubscribeStatus('loading');
        setSubscribeMessage('');

        try {
            const response = await fetch('https://jbuyfsfyd0.execute-api.ca-central-1.amazonaws.com/default/newsletter/join', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ email: email }),
            });

            if (response.ok) {
                setSubscribeStatus('success');
                setSubscribeMessage("Success! You've been added to our mailing list.");
                setEmail('');
                setTimeout(() => {
                    setSubscribeStatus('idle');
                    setSubscribeMessage('');
                }, 5000); // Reset after 5 seconds
            } else {
                const errorData = await response.json().catch(() => ({})); // Catch if response is not json
                setSubscribeStatus('error');
                setSubscribeMessage(errorData.message || 'Something went wrong. Please try again.');
            }
        } catch (error) {
            setSubscribeStatus('error');
            setSubscribeMessage('An unexpected error occurred. Please try again later.');
        }
    };

    const footerLinks = {
        product: [
            { label: 'Features', path: '/features' },
            { label: 'Solutions', path: '/solutions' },
            { label: 'Pricing', path: '/pricing' },
            { label: 'Updates', path: '/updates' },
        ],
        // resources: [
        //     { label: 'Documentation', path: '/docs' },
        //     { label: 'Tutorials', path: '/tutorials' },
        //     { label: 'Case Studies', path: '/case-studies' },
        //     { label: 'FAQs', path: '/faqs' },
        // ],
        company: [
            { label: 'About Us', path: '/about' },
            { label: 'Trust', path: '/trust' },
            // { label: 'Careers', path: '/careers' },
            { label: 'Contact', path: '/contact' },
            // { label: 'Partners', path: '/partners' },
        ],
        legal: [
            { label: 'Privacy Policy', path: '/privacy' },
            { label: 'Terms of Service', path: '/terms' },
            { label: 'Cookie Policy', path: '/cookie-policy' },
            { label: 'EULA', path: '/eula' },
        ],
    };

    const socialLinks = [
        { icon: Linkedin, href: "https://www.linkedin.com/company/ufficient-ai", label: "LinkedIn" },
        { 
            icon: Mail, 
            href: "mailto:info@ufficient.ai",
            label: "Email" 
        },
    ];

    return (
        <div className="main-layout">
            <header className="main-layout-header">
                <nav className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex justify-between h-16">
                        <div className="flex">
                            <Link to="/" className="flex-shrink-0 flex items-center">
                                {/* Mobile Logo - Long Logo */}
                                <img src="/images/Ufficient Long Logo.png" alt="Ufficient Logo" className="h-16 w-auto lg:hidden" />
                                {/* Desktop Logo - Short U Logo */}
                                <img src="/images/Ufficient Long Logo.png" alt="Ufficient Logo" className="h-16 w-auto hidden lg:block" />
                            </Link>
                            <div className="nav-items-container hidden lg:flex">
                                {/* Features Dropdown */}
                                <div className="relative" ref={dropdownRef}>
                                    <button
                                        className={`features-dropdown-trigger ${isDropdownOpen ? 'active' : ''}`}
                                        onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                                    >
                                        Features
                                        <ChevronDown
                                            className={`transition-transform ${isDropdownOpen ? 'rotate-180' : ''}`}
                                            size={16}
                                        />
                                    </button>
                                    <FeaturesDropdown
                                        isOpen={isDropdownOpen}
                                        onClose={() => setIsDropdownOpen(false)}
                                    />
                                </div>

                                {/* Other menu items */}
                                {menuItems.map((item) => (
                                    <NavLink
                                        key={item.path}
                                        to={item.path}
                                        className="nav-link"
                                    >
                                        {item.label}
                                    </NavLink>
                                ))}
                            </div>
                        </div>
                        <div className="hidden lg:ml-6 lg:flex lg:items-center">
                            <a
                                href="https://app.ufficient.ai"
                                className="sign-in-button mr-3"
                            >
                                Sign in
                            </a>
                            <NavLink to="/contact" className="demo-button">
                                Get a demo
                            </NavLink>
                        </div>
                        <div className="-mr-2 flex items-center lg:hidden">
                            <button
                                onClick={() => setIsOpen(!isOpen)}
                                className="p-2 rounded-md text-gray-400 hover:text-gray-500 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-inset"
                                style={{ '--tw-ring-color': 'var(--argus-primary)' }}
                            >
                                <span className="sr-only">Open main menu</span>
                                {isOpen ? <X size={24} /> : <Menu size={24} />}
                            </button>
                        </div>
                    </div>
                </nav>

                {/* Mobile navigation menu */}
                <motion.div
                    className={`lg:hidden overflow-hidden ${isOpen ? 'block' : 'hidden'}`}
                    initial={false}
                    animate={isOpen ? { height: 'auto' } : { height: 0 }}
                    transition={{ duration: 0.3 }}
                >
                    {/* Primary links */}
                    <div className="px-2 pt-2 pb-3 space-y-1 bg-white border-t border-gray-100">
                        <NavLink
                            to="/features"
                            onClick={() => setIsOpen(false)}
                            className="block px-3 py-2 rounded-md text-base font-medium text-gray-700 hover:bg-gray-50"
                        >
                            Features
                        </NavLink>
                        {menuItems.map((item) => (
                            <NavLink
                                key={item.path}
                                to={item.path}
                                onClick={() => setIsOpen(false)}
                                className="block px-3 py-2 rounded-md text-base font-medium text-gray-700 hover:bg-gray-50"
                            >
                                {item.label}
                            </NavLink>
                        ))}
                    </div>

                    {/* Actions */}
                    <div className="px-2 py-3 space-y-1 bg-white border-t border-gray-100">
                        <a
                            href="https://app.ufficient.ai"
                            className="block w-full text-center px-3 py-2 rounded-md font-medium text-blue-600 bg-blue-50 hover:bg-blue-100"
                        >
                            Sign in
                        </a>
                        <NavLink
                            to="/contact"
                            onClick={() => setIsOpen(false)}
                            className="block w-full text-center px-3 py-2 rounded-md font-medium text-white bg-blue-600 hover:bg-blue-700"
                        >
                            Get a demo
                        </NavLink>
                    </div>
                </motion.div>
            </header>

            <main className="flex-grow">
                {children}
            </main>

            <footer className="bg-gradient-to-t from-slate-900 via-slate-800 to-blue-900 text-white pt-16 pb-12">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    {/* Newsletter signup */}
                    <div className="mb-16 lg:mb-20 relative">
                        <div className="absolute inset-0 bg-blue-600 opacity-10 rounded-2xl blur-xl"></div>
                        <div className="relative bg-white/10 backdrop-blur-sm rounded-xl p-8 md:p-10 border border-white/20 shadow-xl">
                            <div className="lg:flex items-center justify-between">
                                <div className="mb-6 lg:mb-0 lg:mr-8 lg:max-w-xl">
                                    <h3 className="text-2xl font-bold mb-2">Stay up to date</h3>
                                    <p className="text-blue-100">Get notified about new features and updates. No spam, just helpful content.</p>
                                </div>
                                <div className="flex-1">
                                    <form onSubmit={(e) => { e.preventDefault(); handleSubscribe(); }} className="sm:flex">
                                        <input
                                            type="email"
                                            placeholder="Enter your email"
                                            className="px-4 py-3 rounded-lg w-full sm:w-auto sm:flex-1 text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500 transition duration-200 mb-3 sm:mb-0 sm:mr-3"
                                            value={email}
                                            onChange={(e) => setEmail(e.target.value)}
                                            disabled={subscribeStatus === 'loading'}
                                        />
                                        <button 
                                            type="submit"
                                            className="bg-blue-600 hover:bg-blue-700 px-6 py-3 rounded-lg font-medium transition duration-200 w-full sm:w-auto text-white disabled:opacity-50"
                                            disabled={subscribeStatus === 'loading'}
                                        >
                                            {subscribeStatus === 'loading' ? 'Subscribing...' : 'Subscribe'}
                                        </button>
                                    </form>
                                    {subscribeMessage && (
                                        <p className={`mt-2 text-sm ${subscribeStatus === 'error' ? 'text-red-300' : 'text-green-300'}`}>
                                            {subscribeMessage}
                                        </p>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Links section */}
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-8 md:gap-12 pb-12 border-b border-white/10">
                        {Object.entries(footerLinks).map(([category, links]) => (
                            <div key={category}>
                                <h3 className="text-lg font-semibold mb-6 capitalize text-blue-200">{category}</h3>
                                <ul className="space-y-4">
                                    {links.map((link) => (
                                        <li key={link.path}>
                                            <Link 
                                                to={link.path} 
                                                className="text-gray-300 hover:text-white transition duration-200 text-sm"
                                            >
                                                {link.label}
                                            </Link>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        ))}
                    </div>

                    {/* Bottom bar */}
                    <div className="mt-12 flex flex-col md:flex-row justify-between items-center">
                        <div className="flex items-center mb-6 md:mb-0">
                            <img src="/Logo 1.png" alt="Ufficient Logo" className="h-8 w-auto mr-3" />
                            <p className="text-sm text-gray-400">&copy; 2025 UFFICIENT INC. All rights reserved.</p>
                        </div>
                        <div className="flex items-center gap-6">
                            <button
                                type="button"
                                onClick={() => window.openCookieSettings?.()}
                                className="text-sm text-gray-400 hover:text-white transition duration-200"
                            >
                                Cookie Settings
                            </button>
                            <div className="flex space-x-4">
                                {socialLinks.map((social, index) => {
                                    const Icon = social.icon;
                                    const isExternal = /^(https?:)?\/\//.test(social.href) || social.href.startsWith('mailto:');
                                    const commonClasses = "w-10 h-10 flex items-center justify-center rounded-full bg-white/10 hover:bg-blue-600 transition-colors duration-300 text-gray-300 hover:text-white";

                                    return isExternal ? (
                                        <a
                                            key={index}
                                            href={social.href}
                                            aria-label={social.label}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className={commonClasses}
                                        >
                                            <Icon size={18} />
                                        </a>
                                    ) : (
                                        <Link
                                            key={index}
                                            to={social.href}
                                            aria-label={social.label}
                                            className={commonClasses}
                                        >
                                            <Icon size={18} />
                                        </Link>
                                    );
                                })}
                            </div>
                        </div>
                    </div>
                </div>
            </footer>
            
            {/* Cookie Consent Banner */}
            <CookieConsent />
        </div>
    );
};

export default MainLayout;